/**
 * localMedia — IndexedDB wrapper for device-only tile photos + voice.
 *
 * Phase 3 (founder spec, 2026-05-24): photos and voice recordings are
 * stored ONLY in the current device's IndexedDB. They never go to the
 * server. Each parent/therapist/child device maintains its own copy of
 * the media; the cloud only stores the board STRUCTURE (label, emoji,
 * color, category, etc.).
 *
 * If a parent uses a second device, photos and voice will need to be
 * re-added on that device (or migrated via the future "send to my other
 * device" QR flow). This is the explicit privacy tradeoff.
 *
 * Storage shape:
 *   key   = `${profileId}:${tileId}`
 *   value = { image_b64?: string, audio_b64?: string, ts: ISO string }
 *
 * Both fields are independently optional, so updating just the photo
 * doesn't wipe the voice.
 */

const DB_NAME = "sonspeak_local_media";
const STORE_NAME = "tile_media";
const DB_VERSION = 1;

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this environment."));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

function _key(profileId, tileId) {
  return `${profileId || ""}:${tileId || ""}`;
}

async function _read(profileId, tileId) {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(_key(profileId, tileId));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function _write(profileId, tileId, patch) {
  try {
    const db = await openDb();
    const existing = (await _read(profileId, tileId)) || {};
    const next = { ...existing, ...patch, ts: new Date().toISOString() };
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const req = tx.objectStore(STORE_NAME).put(next, _key(profileId, tileId));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
    return next;
  } catch {
    return null;
  }
}

// ----- Public API -----------------------------------------------------------

export async function getLocalMedia(profileId, tileId) {
  if (!profileId || !tileId) return null;
  return await _read(profileId, tileId);
}

export async function setLocalImage(profileId, tileId, image_b64) {
  if (!profileId || !tileId) return;
  if (image_b64 == null) {
    // Allow clearing — undefined means no change, null means clear.
    return await _write(profileId, tileId, { image_b64: null });
  }
  return await _write(profileId, tileId, { image_b64 });
}

export async function setLocalAudio(profileId, tileId, audio_b64) {
  if (!profileId || !tileId) return;
  if (audio_b64 == null) {
    return await _write(profileId, tileId, { audio_b64: null });
  }
  return await _write(profileId, tileId, { audio_b64 });
}

export async function deleteLocalMedia(profileId, tileId) {
  if (!profileId || !tileId) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const req = tx.objectStore(STORE_NAME).delete(_key(profileId, tileId));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch { /* swallow */ }
}

/**
 * Hydrate an array of tile rows fetched from the cloud with their local
 * media. Returns a new array; original objects are not mutated.
 *
 * If the cloud row STILL has legacy `custom_image_b64` / `custom_audio_b64`
 * (e.g. mid-migration before the server purge ran), we silently move that
 * media into IndexedDB and clear it from the in-memory copy. The Board
 * code never sees cloud-stored media after this call.
 */
export async function hydrateTilesWithLocalMedia(profileId, tiles) {
  if (!Array.isArray(tiles) || tiles.length === 0) return tiles;
  const out = [];
  for (const t of tiles) {
    let img = null;
    let aud = null;
    // Migrate-in legacy cloud media (rare, only on dirty pre-Phase-3 rows).
    if (t.custom_image_b64) {
      await setLocalImage(profileId, t.id, t.custom_image_b64);
      img = t.custom_image_b64;
    }
    if (t.custom_audio_b64) {
      await setLocalAudio(profileId, t.id, t.custom_audio_b64);
      aud = t.custom_audio_b64;
    }
    if (!img || !aud) {
      const local = await getLocalMedia(profileId, t.id);
      if (!img && local?.image_b64) img = local.image_b64;
      if (!aud && local?.audio_b64) aud = local.audio_b64;
    }
    // Strip legacy fields from the in-memory tile so the rest of the app
    // reads media from `custom_image_b64` / `custom_audio_b64` exactly as
    // before — only the source changed (IndexedDB instead of cloud).
    const clean = { ...t };
    if (img) clean.custom_image_b64 = img; else delete clean.custom_image_b64;
    if (aud) clean.custom_audio_b64 = aud; else delete clean.custom_audio_b64;
    out.push(clean);
  }
  return out;
}

/**
 * Purge IndexedDB entries for a profile. Used when the profile is
 * deleted or the parent triggers "Wipe device" from Settings.
 */
export async function purgeProfileMedia(profileId) {
  if (!profileId) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        const keys = req.result || [];
        const prefix = `${profileId}:`;
        keys
          .filter((k) => typeof k === "string" && k.startsWith(prefix))
          .forEach((k) => store.delete(k));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      };
      req.onerror = () => reject(req.error);
    });
  } catch { /* swallow */ }
}
